import * as React from 'react';
import { Box, Check, Pencil, RotateCcw, Ruler, X } from 'lucide-react';
import {
  useCentrosPortaPalete,
  useContabilizarCentro,
  useRestaurarPortaPalete,
  useSalvarPortaPalete,
  type CentroPortaPalete,
} from '@/hooks/admin';
import { useContexto } from '@/hooks/useContexto';
import { cn, dataHora } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Segmentado } from '@/componentes/ui/segmentado';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  CabecalhoCartao,
  Campo,
  Cartao,
  CorpoCartao,
  Esqueleto,
  Pilula,
  TituloCartao,
} from '@/componentes/ui/primitivos';

/**
 * Tamanho do porta-palete por centro de estoque.
 *
 * O cadastro do CENTRO (nome, tipo, empresa) é do PBS e continua lá — esta
 * tela acrescenta a ele a única coisa que o PBS não tem: a medida da vaga.
 *
 * Por que isso importa: o "% de ocupação" de um localizador é
 * `volume da mercadoria ÷ volume da vaga`. Até agora o divisor era uma
 * constante no código (padrão de mercado 1,35 × 1,00 × 1,50 m). Quem sabe a
 * medida real de cada rack é a operação, não o código.
 *
 * Centro sem medida cadastrada usa o padrão e é marcado como tal — ausência
 * de registro não é erro, é "ainda não medimos este CD".
 */
export function PortaPalete({ habilitado = true }: { habilitado?: boolean }): JSX.Element {
  const consulta = useCentrosPortaPalete(habilitado);
  const salvar = useSalvarPortaPalete();
  const restaurar = useRestaurarPortaPalete();
  const contabilizar = useContabilizarCentro();
  const [editando, definirEditando] = React.useState<number | null>(null);
  const { empresa, empresas } = useContexto();

  /**
   * Abre na EMPRESA em que o usuário está.
   *
   * Quem administra vem de uma empresa selecionada no topo; despejar os
   * centros de todas as empresas obriga a procurar na lista o CD que está
   * bem ali no contexto. "Todas" continua a um clique, porque medir vaga de
   * outra empresa é tarefa real — só não é a mais comum.
   */
  const [escopo, definirEscopo] = React.useState<'empresa' | 'todas'>('empresa');

  const nomeDaEmpresa = React.useMemo(
    () => new Map(empresas.map((e) => [e.empresa, e.nome])),
    [empresas],
  );

  const todos = React.useMemo(() => consulta.data?.centros ?? [], [consulta.data]);
  const visiveis = React.useMemo(
    () =>
      escopo === 'todas' || empresa === null ? todos : todos.filter((c) => c.empresa === empresa),
    [todos, escopo, empresa],
  );

  /** Com uma empresa só na lista, o alternador não tem o que alternar. */
  const varias = new Set(todos.map((c) => c.empresa)).size > 1;

  return (
    <Cartao>
      <CabecalhoCartao>
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primaria-suave text-primaria">
            <Ruler className="h-4 w-4" />
          </span>
          <div>
            <TituloCartao>Centros de estoque</TituloCartao>
            <p className="text-[11.5px] text-textoFraco">
              O tamanho da vaga (divisor do % de ocupação) e quais estoques contam posição.
            </p>
          </div>
        </div>
        <span className="flex flex-wrap items-center gap-2">
          {varias ? (
            <Segmentado
              valor={escopo}
              aoMudar={definirEscopo}
              tamanho="pequeno"
              rotuloAcessivel="Quais centros listar"
              opcoes={[
                {
                  valor: 'empresa',
                  rotulo: `empresa ${empresa ?? ''}`,
                  contagem: todos.filter((c) => c.empresa === empresa).length,
                },
                { valor: 'todas', rotulo: 'todas', contagem: todos.length },
              ]}
            />
          ) : null}
          {consulta.data ? (
            <Pilula tom="neutro">
              padrão {consulta.data.padrao.larguraCm} × {consulta.data.padrao.profundidadeCm} ×{' '}
              {consulta.data.padrao.alturaCm} cm
            </Pilula>
          ) : null}
        </span>
      </CabecalhoCartao>

      {consulta.isLoading ? (
        <CorpoCartao className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Esqueleto key={i} className="h-16" />
          ))}
        </CorpoCartao>
      ) : consulta.isError ? (
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : visiveis.length === 0 ? (
        <EstadoVazio
          titulo={
            todos.length === 0
              ? 'Nenhum centro liberado para o seu usuário'
              : `Nenhum centro na empresa ${empresa}`
          }
          {...(todos.length > 0
            ? {
                detalhe: 'Esta empresa não tem centro endereçado. Veja "todas" para os demais.',
              }
            : {})}
        />
      ) : (
        <ul className="divide-y divide-borda">
          {visiveis.map((c) =>
            editando === c.centroEstoque ? (
              <li key={c.centroEstoque} className="bg-primaria-suave/40 p-4">
                <FormularioMedida
                  centro={c}
                  salvando={salvar.isPending}
                  erro={salvar.error}
                  aoCancelar={() => {
                    salvar.reset();
                    definirEditando(null);
                  }}
                  aoSalvar={(medidas) => {
                    salvar.mutate(
                      { centro: c.centroEstoque, ...medidas },
                      { onSuccess: () => definirEditando(null) },
                    );
                  }}
                />
              </li>
            ) : (
              <li key={c.centroEstoque} className="flex flex-wrap items-center gap-3 px-4 py-3.5">
                <span
                  className={cn(
                    'tabular grid h-10 w-14 shrink-0 place-items-center rounded-lg text-[13px] font-bold',
                    c.cadastrada
                      ? 'gradiente-marca text-white'
                      : 'bg-superficie2 text-textoFraco ring-1 ring-inset ring-borda',
                  )}
                >
                  {c.centroEstoque}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-semibold">{c.nome}</p>
                  <p className="text-[11.5px] text-textoFraco">
                    empresa {c.empresa}
                    {nomeDaEmpresa.get(c.empresa) ? ` · ${nomeDaEmpresa.get(c.empresa)}` : ''} ·
                    tipo {c.tipoEstoque}
                    {c.controlaLocalizador ? ' · endereçado' : ' · sem endereçamento'}
                    {c.cadastrada && c.atualizadoEm
                      ? ` · medido em ${dataHora(c.atualizadoEm)}`
                      : ''}
                  </p>
                  {c.observacao ? (
                    <p className="mt-0.5 truncate text-[11.5px] italic text-textoSuave">
                      {c.observacao}
                    </p>
                  ) : null}
                </div>

                {/*
                  Contabilizar posições: o cadastro que o PBS não tem.
                  Recebimento e trânsito têm posição e saldo, mas é mercadoria
                  de passagem — somá-los faz o galpão parecer mais cheio do que
                  está. Fica aqui, na linha do centro, porque é olhando a lista
                  de centros que a operação reconhece qual é qual.
                */}
                <label
                  className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-borda px-2 py-1.5 text-[11.5px] transition-colors hover:border-primaria/40"
                  title={
                    c.contabilizaPosicoes
                      ? 'As posições deste centro entram na ocupação da empresa'
                      : 'Este centro fica fora da ocupação da empresa'
                  }
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-[rgb(var(--cor-primaria))]"
                    checked={c.contabilizaPosicoes}
                    disabled={contabilizar.isPending}
                    onChange={(e) =>
                      contabilizar.mutate({
                        centro: c.centroEstoque,
                        contabiliza: e.currentTarget.checked,
                      })
                    }
                  />
                  <span className={c.contabilizaPosicoes ? '' : 'text-textoFraco'}>
                    contabiliza posições
                  </span>
                </label>

                <div className="flex items-center gap-4">
                  <span className="text-right leading-tight">
                    <span className="tabular block font-mono text-[14px] font-bold">
                      {c.larguraCm} × {c.profundidadeCm} × {c.alturaCm} cm
                    </span>
                    {/* Mesma razao da previa do formulario: abaixo de 1 dm³,
                        "0,000 m³" le-se como erro de calculo em vez de medida
                        impossivel. */}
                    <span className="tabular block text-[11px] text-textoFraco">
                      {c.volumeM3 < 0.001
                        ? `${(c.larguraCm * c.profundidadeCm * c.alturaCm).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} cm³ por vaga`
                        : `${c.volumeM3.toLocaleString('pt-BR', { minimumFractionDigits: 3 })} m³ por vaga`}
                    </span>
                  </span>

                  {/*
                    Medida implausível já GRAVADA — o aviso precisa estar aqui, e
                    não só no formulário, porque quem vê o estrago vê no MAPA
                    (tudo vermelho) e não tem como ligar aquilo a esta linha.
                  */}
                  {c.cadastrada && Math.min(c.larguraCm, c.profundidadeCm, c.alturaCm) < 20 ? (
                    <Pilula tom="alerta" title="A medida parece estar em metros, não em centímetros. Com uma vaga desse tamanho o mapa marca todas as posições como cheias.">
                      medida em metros?
                    </Pilula>
                  ) : c.cadastrada ? (
                    <Pilula tom="ok">medido</Pilula>
                  ) : (
                    <Pilula tom="neutro">padrão</Pilula>
                  )}

                  <div className="flex items-center gap-1">
                    <Botao
                      variante="secundaria"
                      tamanho="pequeno"
                      onClick={() => {
                        salvar.reset();
                        definirEditando(c.centroEstoque);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      {c.cadastrada ? 'Editar' : 'Medir'}
                    </Botao>
                    {c.cadastrada ? (
                      <Botao
                        variante="fantasma"
                        tamanho="iconePequeno"
                        aria-label={`Voltar o centro ${c.centroEstoque} ao padrão do sistema`}
                        title="Voltar ao padrão do sistema"
                        carregando={restaurar.isPending}
                        onClick={() => restaurar.mutate(c.centroEstoque)}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Botao>
                    ) : null}
                  </div>
                </div>
              </li>
            ),
          )}
        </ul>
      )}
    </Cartao>
  );
}

interface Medidas {
  larguraCm: number;
  profundidadeCm: number;
  alturaCm: number;
  observacao: string | null;
}

function FormularioMedida({
  centro,
  salvando,
  erro,
  aoSalvar,
  aoCancelar,
}: {
  centro: CentroPortaPalete;
  salvando: boolean;
  erro: unknown;
  aoSalvar: (medidas: Medidas) => void;
  aoCancelar: () => void;
}): JSX.Element {
  const [largura, definirLargura] = React.useState(String(centro.larguraCm));
  const [profundidade, definirProfundidade] = React.useState(String(centro.profundidadeCm));
  const [altura, definirAltura] = React.useState(String(centro.alturaCm));
  const [observacao, definirObservacao] = React.useState(centro.observacao ?? '');

  const n = (v: string): number => Number(v.replace(',', '.'));

  /*
   * O piso de 20 cm é a trava contra o erro de UNIDADE, não contra descuido.
   *
   * O centro 3201 foi salvo com 1,10 × 3,42 × 2,33 — as medidas certas do
   * rack, só que em metros. A vaga virou um cubo de 1 cm, todo item passou a
   * ocupar mais de 100% dela e o mapa inteiro ficou vermelho, inclusive
   * posição com uma unidade. O servidor também recusa; aqui o aviso chega
   * antes, com o número já convertido, que é o que a pessoa precisa saber.
   */
  const MINIMO_CM = 20;
  const medidas = [largura, profundidade, altura];
  const numericas = medidas.every((v) => Number.isFinite(n(v)) && n(v) > 0);
  const pareceMetros = numericas && medidas.some((v) => n(v) < MINIMO_CM);
  const valido = numericas && !pareceMetros;
  /*
   * Prévia do volume: o número que realmente entra na conta de ocupação.
   *
   * Sai de `numericas`, e NÃO de `valido`, de propósito — quem digitou a
   * medida errada precisa VER o resultado dela; esconder atrás de um traço
   * só troca um número sem sentido por nenhum.
   */
  const volumeCm3 = numericas ? n(largura) * n(profundidade) * n(altura) : 0;
  const volume = volumeCm3 / 1_000_000;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valido) return;
        aoSalvar({
          larguraCm: n(largura),
          profundidadeCm: n(profundidade),
          alturaCm: n(altura),
          observacao: observacao.trim() || null,
        });
      }}
      className="space-y-3"
    >
      <p className="flex items-center gap-2 text-[13px] font-semibold">
        <Box className="h-4 w-4 text-primaria" />
        {centro.centroEstoque} · {centro.nome}
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <CampoMedida rotulo="Largura da vaga" valor={largura} aoMudar={definirLargura} />
        <span className="pb-2.5 text-textoSuave">×</span>
        <CampoMedida rotulo="Profundidade" valor={profundidade} aoMudar={definirProfundidade} />
        <span className="pb-2.5 text-textoSuave">×</span>
        <CampoMedida rotulo="Altura útil do nível" valor={altura} aoMudar={definirAltura} />

        {/*
          O VOLUME PRECISA SER LEGÍVEL, INCLUSIVE QUANDO É ABSURDO.
          
          Com 1,10 × 3,42 × 2,33 cm a vaga tem 8,8 cm³ — um dado de jogar. Em
          m³ isso é 0,0000088, e com três casas o campo escrevia "0,000 m³".
          Quem leu entendeu que a conta tinha falhado, quando na verdade ela
          estava certa e a MEDIDA é que era impossível. Abaixo de 1 dm³
          mostramos em cm³, que é a escala em que o absurdo se enxerga.
        */}
        <span className="pb-1 leading-tight">
          <span className="rotulo block">Volume da vaga</span>
          <span
            className={cn(
              'tabular text-[15px] font-bold',
              !numericas ? 'text-primaria' : pareceMetros ? 'text-status-atencao' : 'text-primaria',
            )}
          >
            {!numericas
              ? '— m³'
              : volume < 0.001
                ? `${volumeCm3.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} cm³`
                : `${volume.toLocaleString('pt-BR', { minimumFractionDigits: 3 })} m³`}
          </span>
        </span>
      </div>

      <label className="block">
        <span className="rotulo mb-1 block">Observação (opcional)</span>
        <Campo
          value={observacao}
          onChange={(e) => definirObservacao(e.target.value)}
          placeholder="De onde veio a medida. Ex.: rack Águia, medido em 12/09 pelo João."
          maxLength={300}
          className="min-h-[38px] text-[13px]"
        />
      </label>

      <p className="text-[11.5px] leading-relaxed text-textoFraco">
        Medidas em centímetros, da vaga de UM localizador — não do vão inteiro. Um vão de 2,70 m com
        dois paletes lado a lado dá 135 cm por vaga. Alterar aqui recalcula o % de ocupação do mapa
        e dos relatórios deste centro.
      </p>

      {pareceMetros ? (
        <p className="rounded-lg border border-atencao/40 bg-atencao/10 px-3 py-2 text-[12px] leading-relaxed">
          <strong>Isso parece estar em metros.</strong> O campo é em CENTÍMETROS — uma vaga de{' '}
          {medidas.map((v) => n(v).toLocaleString('pt-BR')).join(' × ')} cm seria do tamanho de
          uma caixa de fósforos, e o mapa marcaria todas as posições como cheias. Em centímetros
          ficaria{' '}
          <strong>{medidas.map((v) => Math.round(n(v) * 100)).join(' × ')} cm</strong>.
        </p>
      ) : null}

      {erro ? <EstadoErro erro={erro} className="py-4" /> : null}

      <div className="flex items-center gap-2">
        <Botao type="submit" variante="primaria" tamanho="pequeno" disabled={!valido} carregando={salvando}>
          {!salvando ? <Check className="h-4 w-4" /> : null}
          Salvar medida
        </Botao>
        <Botao type="button" variante="fantasma" tamanho="pequeno" onClick={aoCancelar}>
          <X className="h-4 w-4" />
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

function CampoMedida({
  rotulo,
  valor,
  aoMudar,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
}): JSX.Element {
  return (
    <label className="block">
      <span className="rotulo mb-1 block">{rotulo}</span>
      <span className="relative block">
        <Campo
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          inputMode="decimal"
          className="tabular w-[130px] pr-9 text-[14px] font-semibold"
          aria-label={`${rotulo} em centímetros`}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-textoSuave">
          cm
        </span>
      </span>
    </label>
  );
}
